import React, { useMemo, useState } from 'react';
import { Modal, Select, Alert, Spin } from 'antd';
import { useSalesOrders } from '../../hooks/useSalesOrder';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE, uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import type { SalesOrder } from '@/services/salesOrder.service';

interface SelectSalesOrderForAdvanceModalProps {
  open: boolean;
  onClose: () => void;
  onSelectSalesOrder: (salesOrderId: string) => void;
}

export const SelectSalesOrderForAdvanceModal: React.FC<SelectSalesOrderForAdvanceModalProps> = ({
  open,
  onClose,
  onSelectSalesOrder,
}) => {
  const { baseCurrency } = useCurrency();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: orders = [], isLoading } = useSalesOrders(
    open ? { approval_status: 'Approved' } : undefined
  );

  const eligibleOrders = useMemo(() => {
    return orders.filter(
      (o: SalesOrder) =>
        Number(o.advance_payment_percentage ?? 0) > 0 || Number(o.advance_payment_amount ?? 0) > 0
    );
  }, [orders]);

  const currencySymbol =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    AED_CURRENCY_CODE;

  const handleContinue = () => {
    if (selectedId) {
      onSelectSalesOrder(selectedId);
      setSelectedId(null);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  return (
    <Modal
      title="Create Proforma Invoice – Select Customer Order"
      open={open}
      onCancel={handleClose}
      onOk={handleContinue}
      okText="Continue"
      okButtonProps={{ disabled: !selectedId }}
      cancelText="Cancel"
      width={520}
      destroyOnClose
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {!isLoading && eligibleOrders.length === 0 && (
        <Alert
          type="info"
          message="No approved Customer Orders with advance found"
          description="Only approved Customer Orders with advance % or advance amount defined are shown. Create or edit a Customer Order to add advance terms, then approve it."
          showIcon
        />
      )}
      {!isLoading && eligibleOrders.length > 0 && (
        <>
          <p style={{ marginBottom: 8 }}>
            Select a Customer Order to create an advance invoice for:
          </p>
          <Select
            placeholder="Select Customer Order"
            value={selectedId || undefined}
            onChange={(v) => setSelectedId(v ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            options={eligibleOrders.map((o: SalesOrder) => {
              const advanceAmt = Number(o.advance_payment_amount ?? 0) ||
                (Number(o.advance_payment_percentage ?? 0) / 100) * Number(o.grand_total ?? 0);
              return {
                value: o.id,
                label: `${o.sales_order_number} – ${o.customer_name ?? '-'} – Advance ${currencySymbol} ${advanceAmt.toFixed(2)}`,
              };
            })}
          />
        </>
      )}
    </Modal>
  );
};
